(function () {
  'use strict';

  const CART_ENDPOINTS = ['/cart/add'];
  const config = window.tttInterceptorConfig || {};
  const rules = Array.isArray(config.rules) ? config.rules : [];

  if (!rules.length) return;

  // Build included/excluded variant ID sets
  const includedVariants = new Set(
    (Array.isArray(config.includedVariantIds) ? config.includedVariantIds : []).map(String)
  );
  const excludedVariants = new Set(
    (Array.isArray(config.excludedVariantIds) ? config.excludedVariantIds : []).map(String)
  );

  function isVariantAllowed(variantId) {
    const id = String(variantId);
    if (excludedVariants.has(id)) return false;
    if (includedVariants.size === 0) return true;
    return includedVariants.has(id);
  }

  // Use the first rule (multiple rules reserved for future use)
  const rule = rules[0];
  const SOURCE_KEY = rule.sourceKey || '__kaching_bundles';
  const SOURCE_VALUE = rule.sourceValue || '';
  const STRIP_SOURCE_KEY = rule.stripSourceKey || false;
  const TTT_KEY = rule.replacementKey || '_ttt-offer-b';
  const TTT_VALUE = rule.replacementValue || 'True';
  const ALWAYS_ADD = rule.alwaysAdd || false;
  const ONE_TIME_ONLY = rule.oneTimeOnly || false;

  function isCartEndpoint(url) {
    const str = (typeof url === 'string' ? url : url?.url) || '';
    return CART_ENDPOINTS.some((ep) => str.includes(ep));
  }

  function sourceMatches(val) {
    return !SOURCE_VALUE || val === SOURCE_VALUE;
  }

  // --- FormData handler ---
  function processFormData(fd) {
    const variantId = fd.get('id') || fd.get('variant_id');
    if (variantId && !isVariantAllowed(variantId)) {
      return { data: fd, modified: false };
    }
    // Skip items with a selling plan (subscriptions)
    if (ONE_TIME_ONLY) {
      const sellingPlan = fd.get('selling_plan');
      if (sellingPlan && sellingPlan !== '0' && sellingPlan !== '') {
        return { data: fd, modified: false };
      }
    }

    let modified = false;
    let foundSource = false;
    const next = new FormData();

    for (const [key, val] of fd.entries()) {
      if (key === `properties[${SOURCE_KEY}]` && sourceMatches(val)) {
        foundSource = true;
        if (!STRIP_SOURCE_KEY) next.append(key, val);
      } else {
        next.append(key, val);
      }
    }

    if (foundSource || ALWAYS_ADD) {
      next.append(`properties[${TTT_KEY}]`, TTT_VALUE);
      modified = true;
    }

    return { data: next, modified };
  }

  // --- JSON handler ---
  function processJSON(bodyStr) {
    let parsed;
    try { parsed = JSON.parse(bodyStr); } catch { return { data: bodyStr, modified: false }; }

    let modified = false;
    const items = parsed.items ?? (parsed.id !== undefined ? [parsed] : []);

    for (const item of items) {
      const variantId = item.id || item.variant_id;
      if (variantId && !isVariantAllowed(variantId)) continue;
      // Skip items with a selling plan (subscriptions)
      if (ONE_TIME_ONLY && item.selling_plan && String(item.selling_plan) !== '0') continue;

      const props = item.properties;
      const hasSource = props && SOURCE_KEY in props && sourceMatches(props[SOURCE_KEY]);

      if (hasSource) {
        if (STRIP_SOURCE_KEY) delete props[SOURCE_KEY];
        props[TTT_KEY] = TTT_VALUE;
        modified = true;
      } else if (ALWAYS_ADD) {
        if (!item.properties) item.properties = {};
        item.properties[TTT_KEY] = TTT_VALUE;
        modified = true;
      }
    }

    return { data: modified ? JSON.stringify(parsed) : bodyStr, modified };
  }

  // --- URL-encoded handler ---
  function processURLEncoded(body) {
    const params = typeof body === 'string' ? new URLSearchParams(body) : body;
    const variantId = params.get('id') || params.get('variant_id');
    if (variantId && !isVariantAllowed(variantId)) {
      return { data: params, modified: false };
    }
    // Skip items with a selling plan (subscriptions)
    if (ONE_TIME_ONLY) {
      const sellingPlan = params.get('selling_plan');
      if (sellingPlan && sellingPlan !== '0' && sellingPlan !== '') {
        return { data: params, modified: false };
      }
    }

    let foundSource = false;
    const next = new URLSearchParams();

    for (const [key, val] of params.entries()) {
      if (key === `properties[${SOURCE_KEY}]` && sourceMatches(val)) {
        foundSource = true;
        if (!STRIP_SOURCE_KEY) next.append(key, val);
      } else {
        next.append(key, val);
      }
    }

    if (foundSource || ALWAYS_ADD) {
      next.append(`properties[${TTT_KEY}]`, TTT_VALUE);
    }

    return { data: next, modified: foundSource || ALWAYS_ADD };
  }

  // --- Fetch intercept ---
  const _fetch = window.fetch.bind(window);
  window.fetch = function (input, init = {}) {
    if (!isCartEndpoint(input)) return _fetch(input, init);

    const body = init.body;
    if (body instanceof FormData) {
      const { data, modified } = processFormData(body);
      if (modified) init = { ...init, body: data };
    } else if (body instanceof URLSearchParams) {
      const { data, modified } = processURLEncoded(body);
      if (modified) init = { ...init, body: data };
    } else if (typeof body === 'string') {
      const jsonResult = processJSON(body);
      if (jsonResult.modified) {
        init = { ...init, body: jsonResult.data };
      } else {
        const { data, modified } = processURLEncoded(body);
        if (modified) init = { ...init, body: data.toString() };
      }
    }
    return _fetch(input, init);
  };

  // --- XHR intercept (safety net for third-party apps) ---
  const _open = XMLHttpRequest.prototype.open;
  const _send = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    this._ttt_url = url;
    return _open.call(this, method, url, ...rest);
  };

  XMLHttpRequest.prototype.send = function (body) {
    if (isCartEndpoint(this._ttt_url)) {
      if (body instanceof FormData) {
        const { data } = processFormData(body);
        return _send.call(this, data);
      } else if (body instanceof URLSearchParams) {
        const { data } = processURLEncoded(body);
        return _send.call(this, data);
      } else if (typeof body === 'string') {
        const jsonResult = processJSON(body);
        if (jsonResult.modified) {
          return _send.call(this, jsonResult.data);
        } else {
          const { data, modified } = processURLEncoded(body);
          if (modified) return _send.call(this, data.toString());
        }
      }
    }
    return _send.call(this, body);
  };
})();
